/**
 * Backend Health Check Utility
 *
 * Provides functions to check if the backend service is available.
 * Used to determine whether to show the chatbot widget.
 */

/**
 * Check if the backend is available by calling the public status endpoint
 *
 * @param {string} apiBaseUrl - The base URL of the API (from env var)
 * @param {number} timeoutMs - Timeout in milliseconds (default: 3000ms)
 * @returns {Promise<boolean>} - true if backend is available, false otherwise
 */
export async function checkBackendAvailability(apiBaseUrl, timeoutMs = 3000) {
  // Create an AbortController for timeout handling
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${apiBaseUrl}/api/status`, {
      method: 'GET',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
      },
      // Don't send credentials for this lightweight check
      credentials: 'omit',
    });

    clearTimeout(timeoutId);

    // Check if response is successful and has expected shape
    if (response.ok) {
      const data = await response.json();
      return data.available === true;
    }

    return false;
  } catch (error) {
    clearTimeout(timeoutId);

    // Network errors, timeouts, CORS issues all indicate backend unavailable
    if (error.name === 'AbortError') {
      console.log('Backend health check timed out - assuming offline');
    } else {
      console.log('Backend health check failed:', error.message);
    }

    return false;
  }
}
